import React, { useState, useEffect } from "react";
import api from "./services/api";
import Invoice from "./components/Invoice";

export default function AdminInvoices() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get("/orders");
      // For invoices, we usually only care about successful/paid orders, 
      // but listing all allows better auditing.
      setOrders(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => 
    order.id?.toString().includes(searchTerm) || 
    order.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePrint = (order) => {
    setSelectedOrder(order);
    // Give it a tiny bit of time to render before printing
    setTimeout(() => {
      window.print();
    }, 100);
  };

  if (loading) return <div className="p-8 text-center text-raya-text-secondary">Loading invoices...</div>;
  if (error) return <div className="p-8 text-center text-red-600">Error: {error}</div>;

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8 no-print">
        <div>
          <h1 className="text-3xl font-black text-raya-text-primary tracking-tight">
            Tax Invoices & Tracking
          </h1>
          <p className="text-raya-text-secondary mt-1">Manage and track successful order invoices for tax compliance.</p>
        </div>
        
        <div className="relative">
          <input
            type="text"
            placeholder="Search Order ID or Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 bg-raya-card border border-raya-border rounded-xl text-raya-text-primary focus:ring-2 focus:ring-raya-accent/50 outline-none w-64 shadow-sm"
          />
          <svg className="w-5 h-5 absolute left-3 top-2.5 text-raya-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div className="bg-raya-card rounded-2xl shadow-xl border border-raya-border overflow-hidden no-print">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-raya-bg-off">
              <tr>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">ID</th>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">Date</th>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">Customer</th>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">Subtotal</th>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">Tax</th>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary">Total</th>
                <th className="p-4 font-bold text-xs uppercase tracking-widest text-raya-text-tertiary text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-raya-border">
              {filteredOrders.map((order) => {
                // Map DB snake_case to component camelCase if needed
                const orderForInvoice = {
                  ...order,
                  userEmail: order.user_email,
                  taxAmount: order.tax_amount,
                  taxBreakdown: order.tax_breakdown || [],
                  shippingAddress: order.shipping_address
                };

                return (
                  <tr key={order.id} className="hover:bg-raya-accent/5 transition-colors group">
                    <td className="p-4 font-mono text-sm text-raya-text-tertiary">#{order.id}</td>
                    <td className="p-4 text-sm text-raya-text-secondary">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 font-medium text-raya-text-primary truncate max-w-[150px]">
                      {order.user_email}
                    </td>
                    <td className="p-4 text-sm text-raya-text-secondary">
                      ${Number(order.subtotal || order.total - (order.tax_amount || 0)).toFixed(2)}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-raya-accent text-sm">${Number(order.tax_amount || 0).toFixed(2)}</span>
                        {order.tax_breakdown?.some(t => t.amount > 0) && (
                           <span className="text-[10px] text-raya-text-tertiary italic">Itemized</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 font-black text-raya-text-primary">
                      ${Number(order.total).toFixed(2)}
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handlePrint(orderForInvoice)}
                        className="p-2 rounded-lg bg-raya-bg-off border border-raya-border text-raya-text-secondary hover:text-raya-accent hover:border-raya-accent transition-all transform active:scale-90"
                        title="Print PDF Invoice"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredOrders.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-raya-text-tertiary italic">
                    No orders matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hidden container for printing */}
      <div className="hidden print:block">
        {selectedOrder && <Invoice order={selectedOrder} />}
      </div>
      
      {/* Styles to handle the layout during preview/print */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .print\\:block { display: block !important; }
        }
      `}</style>
    </div>
  );
}
